import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Mail, Lock, ArrowRight } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Checkbox } from "../../components/ui/checkbox";
import Logo from "../../Assets/imgs/uprestro-logo.webp";
import {
  APL_LINK,
  server_post_data,
  login_to_pos,
} from "../../ServiceConnection/serviceconnection.js";
import {
  check_vaild_save,
  combiled_form_data,
  handleLinkClick,
} from "../../CommonJquery/CommonJquery";
import {
  decryptData,
  encryptData,
  storeData,
} from "../../LocalConnection/LocalConnection.js";
import Cookies from "js-cookie";
import CryptoJS from "crypto-js";

const LoginPage = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    rememberMe: false,
  });

  // Email validation function
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Show custom toast notification
  const showNotification = (message, type = "error") => {
    // Remove any existing toasts
    const existingToasts = document.querySelectorAll(".custom-toast");
    existingToasts.forEach((toast) => toast.remove());

    // Create toast element
    const toast = document.createElement("div");
    toast.className = `custom-toast fixed top-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg transform transition-all duration-300 ${
      type === "error"
        ? "bg-red-500 text-white"
        : type === "success"
          ? "bg-green-500 text-white"
          : "bg-blue-500 text-white"
    }`;
    toast.style.transform = "translateX(400px)";
    toast.innerHTML = `
      <div class="flex items-center gap-2">
        <span class="font-medium">${message}</span>
      </div>
    `;

    document.body.appendChild(toast);

    // Animate in
    setTimeout(() => {
      toast.style.transform = "translateX(0)";
    }, 10);

    // Remove after 3 seconds
    setTimeout(() => {
      toast.style.transform = "translateX(400px)";
      setTimeout(() => {
        if (toast && toast.parentNode) {
          document.body.removeChild(toast);
        }
      }, 300);
    }, 3000);
  };

  // Decrypt email from cookies
  const decryptEmail = (encryptedEmail) => {
    try {
      const encrypted = CryptoJS.AES.decrypt(encryptedEmail, "secret_key");
      const decryptedEmail = encrypted.toString(CryptoJS.enc.Utf8);
      const [email, salt] = decryptedEmail.split("::");
      return email;
    } catch (error) {
      return "";
    }
  };

  // Encrypt password
  const encryptPassword = (password) => {
    const salt = CryptoJS.lib.WordArray.random(128 / 8);
    const passwordWithSalt = password + "::" + salt.toString(CryptoJS.enc.Hex);
    const encryptedPassword = CryptoJS.AES.encrypt(
      passwordWithSalt,
      "secret_key",
    ).toString();
    return encryptedPassword;
  };

  // Decrypt password from cookies
  const decryptPassword = (encryptedPassword) => {
    try {
      const encrypted = CryptoJS.AES.decrypt(encryptedPassword, "secret_key");
      const decryptedEmail = encrypted.toString(CryptoJS.enc.Utf8);
      const [passwordWithSalt, salt] = decryptedEmail.split("::");
      const password = passwordWithSalt.split("::")[0];
      return password;
    } catch (error) {
      return "";
    }
  };

  // Load saved credentials from cookies ONLY if Remember Me was previously checked
  useEffect(() => {
    const email = Cookies.get("email");
    const password = Cookies.get("password");

    // Only load credentials if both exist (meaning Remember Me was checked previously)
    if (email && password) {
      const decryptedEmail = decryptEmail(email);
      const decryptedPassword = decryptPassword(password);

      if (decryptedEmail && decryptedPassword) {
        setFormData({
          email: decryptedEmail,
          password: decryptedPassword,
          rememberMe: true, // Auto-check Remember Me if credentials are loaded
        });
      }
    }
  }, []); // Only run once on mount

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Validate email on change
    if (name === "email") {
      if (value && !validateEmail(value)) {
        setEmailError("Please enter a valid email address");
      } else {
        setEmailError("");
      }
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate email format before submission
    if (!validateEmail(formData.email)) {
      setEmailError("Please enter a valid email address");
      showNotification("Please enter a valid email address", "error");
      return;
    }

    setIsLoading(true);

    // Create a temporary container for the form
    const tempContainer = document.createElement("div");
    tempContainer.style.display = "none";
    document.body.appendChild(tempContainer);

    // Create form element for validation
    const form = document.createElement("form");
    form.id = "loginFormData";

    const emailInput = document.createElement("input");
    emailInput.name = "useremail";
    emailInput.value = formData.email;
    emailInput.className = "trio_email trio_mandatory";
    form.appendChild(emailInput);

    const passwordInput = document.createElement("input");
    passwordInput.name = "userpassword";
    passwordInput.value = formData.password;
    passwordInput.className = "trio_password trio_mandatory";
    form.appendChild(passwordInput);

    tempContainer.appendChild(form);

    // Pass the form ID string, not the form element
    let vaild_data = check_vaild_save("loginFormData");

    if (vaild_data) {
      let fd_from = combiled_form_data("loginFormData", null);

      try {
        const Response = await server_post_data(login_to_pos, fd_from);

        if (Response.data.error) {
          showNotification(Response.data.message, "error");
          setIsLoading(false);
        } else {
          const decrypted = decryptData(Response.data.message);
          let data_restaurant = decrypted.data_restaurant;
          let tokens = decrypted.tokens || {};
          let Response_designation = decrypted.data_designation;
          let Response_data = decrypted.data_admin;

          // Store admin data
          storeData("admin_id", encryptData(Response_data.primary_id));
          storeData("admin_name", encryptData(Response_data.admin_name));
          storeData(
            "designation_name",
            encryptData(Response_designation.designation_name),
          );
          storeData(
            "access_permission",
            encryptData(Response_designation.dashboard_access_list),
          );
          storeData(
            "department_ids",
            encryptData(Response_designation.department_ids),
          );
          storeData(
            "default_restaurant_id",
            encryptData(data_restaurant.primary_id),
          );
          storeData(
            "restaurant_name",
            encryptData(data_restaurant.restaurant_name),
          );
          storeData("packageName", encryptData(data_restaurant.database_name));
          storeData("access_token", tokens.access);
          storeData("refresh_token", tokens.refresh);

          // Handle Remember Me
          if (formData.rememberMe) {
            // Save credentials in cookies if Remember Me is checked
            const encryptedEmail = CryptoJS.AES.encrypt(
              formData.email,
              "secret_key",
            ).toString();
            const encryptedPassword = encryptPassword(formData.password);

            Cookies.set("email", encryptedEmail, { expires: 30 });
            Cookies.set("password", encryptedPassword, { expires: 30 });
          } else {
            // Remove cookies if Remember Me is NOT checked
            Cookies.remove("email");
            Cookies.remove("password");
          }

          showNotification("Login successful! Redirecting...", "success");
          if (Response_designation.dashboard_landing_url) {
            setTimeout(() => {
              handleLinkClick("/" + Response_designation.dashboard_landing_url);
              navigate("/" + Response_designation.dashboard_landing_url);
            }, 1000);
          } else {
            setTimeout(() => {
              handleLinkClick("/Dashboard");
              navigate("/Dashboard");
            }, 1000);
          }
        }
      } catch (error) {
        console.log(error);
        showNotification("An error occurred. Please try again.", "error");
        setIsLoading(false);
      }
    } else {
      showNotification(
        "Please fill in all required fields correctly.",
        "error",
      );
      setIsLoading(false);
    }

    // Clean up - remove the temporary container
    if (tempContainer && tempContainer.parentNode) {
      document.body.removeChild(tempContainer);
    }
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <div className="min-h-screen w-full flex">
      {/* Left Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md space-y-8">
          {/* Logo & Brand */}
          <div className="text-center space-y-2">
            <img
              src={Logo}
              alt="UpRestro Logo"
              className="h-8 mx-auto w-auto object-contain"
            />
            <p className="text-muted-foreground text-sm">
              Restaurant Management System
            </p>
          </div>

          {/* Login Card */}
          <Card className="border-1 shadow-lg">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl font-bold text-center">
                Welcome Back
              </CardTitle>
              <CardDescription className="text-center">
                Enter your credentials to access your account
              </CardDescription>
            </CardHeader>

            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Email Field */}
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="admin@uprestro.com"
                      value={formData.email}
                      onChange={handleChange}
                      onKeyPress={handleKeyPress}
                      className={`pl-10 ${emailError ? "border-red-500" : ""}`}
                      required
                      autoFocus
                    />
                  </div>
                  {emailError && (
                    <p className="text-red-500 text-xs mt-1">{emailError}</p>
                  )}
                </div>

                {/* Password Field */}
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={handleChange}
                      onKeyPress={handleKeyPress}
                      className="pl-10 pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-3 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Remember Me & Forgot Password */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="rememberMe"
                      name="rememberMe"
                      checked={formData.rememberMe}
                      onCheckedChange={(checked) =>
                        setFormData((prev) => ({
                          ...prev,
                          rememberMe: checked,
                        }))
                      }
                    />
                    <label
                      htmlFor="rememberMe"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      Remember me
                    </label>
                  </div>
                  <Link
                    to="/forgot-password"
                    className="text-sm text-cyan-600 hover:text-cyan-700 font-medium transition-colors"
                  >
                    Forgot password?
                  </Link>
                </div>

                {/* Login Button */}
                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-cyan-500 to-teal-600 hover:from-cyan-600 hover:to-teal-700 text-white font-semibold shadow-lg"
                  size="lg"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Signing in...
                    </>
                  ) : (
                    <>
                      Sign In
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Right Side - Branding/Image with Restaurant Background */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?q=80&w=2070&auto=format&fit=crop')",
          }}
        />

        {/* Black Overlay with Gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/70 to-black/80" />

        {/* Cyan/Teal Accent Overlay (subtle) */}
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-600/20 via-teal-600/10 to-cyan-700/20" />

        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-red-600/10 rounded-full blur-3xl" />

        <div className="relative z-10 flex items-center justify-center p-12 w-full">
          {/* Hero Text */}
          <div className="text-center space-y-4">
            <h2 className="text-4xl font-bold leading-tight drop-shadow-lg text-white/90">
              Manage Your Restaurant with Confidence
            </h2>
            <p className="text-lg text-white/90">
              Streamline operations, track inventory, manage recipes, and grow
              your business with our comprehensive restaurant management
              solution.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
